// Owner: ai-client (wave 1), copilot-panel (wave 2). `<AskAiButton scope=… />` (plan §3.3, §7.3): the one ✦
// entry point of every editor top bar and screen. Inside an editor with inline AI (`InlineAiProvider`) it
// opens the prompt bar at the caret; elsewhere it opens the Copilot panel with the scope. It follows
// `useAiAvailability()`: nothing for members while AI is off, one "Connect AI" link for admins where
// `showConnect` is set (the rail), and with the budget used up the button still opens the Copilot panel
// (read-only, with a banner that says why).
// Light module: no run machinery.
import { Link } from '@tanstack/react-router';
import { Button, Icon, IconButton, Tooltip, useIsCompact } from '@dataroom/ui';
import { formatShortcut, t } from '../lib/i18n';
import { openLayer } from '../shell/overlays';
import { useAiAvailability } from './availability';
import { useInlineAiTarget } from './ghost/context';
import type { AiScope } from './run/controller';
import './ai-client.css';

/** The Copilot panel registers `overlays/copilot.tsx` (copilot-panel); until then there is no panel. */
const COPILOT_PRESENT = Object.keys(import.meta.glob('../overlays/copilot.tsx')).length > 0;

export type AskAiButtonProps = {
  scope?: AiScope;
  /** `icon` (top bars, default), `button` (with the label) or `rail` (full-width row). */
  variant?: 'icon' | 'button' | 'rail';
  /** Show "Connect AI" to admins while AI is not connected (one place per screen, e.g. the rail). */
  showConnect?: boolean;
  /** Prefilled prompt for inline AI (it runs at once). */
  prompt?: string;
  className?: string;
};

export function AskAiButton({ scope, variant = 'icon', showConnect, prompt, className }: AskAiButtonProps) {
  const inline = useInlineAiTarget();
  const availability = useAiAvailability(inline ? 'inline' : 'agent');
  const canOpen = Boolean(inline?.enabled) || COPILOT_PRESENT;
  const label = t('aiClient.ask.label');
  // Phones have no ⌘J: no key hint there.
  const compact = useIsCompact();

  if (availability.state === 'connect' && showConnect)
    return (
      <Link to="/settings/ai" className={`aic-connect ${className ?? ''}`} data-testid="ai-connect">
        <Icon name="sparkle" size={14} />
        {t('aiClient.ask.connect')}
      </Link>
    );
  // With the budget used up the entry point stays live: it opens the Copilot panel, which keeps the
  // conversation history readable and says (in a banner) what is used up and who can raise it.
  const limited = availability.state === 'limited';
  if ((availability.state !== 'ready' && !limited) || (limited ? !COPILOT_PRESENT : !canOpen)) return null;

  const run = () => {
    if (inline?.enabled && !limited) inline.open(prompt);
    else openLayer('copilot', { scope });
  };
  const reason = limited ? t('aiClient.ask.budget') : null;
  if (variant === 'icon')
    return (
      <IconButton
        icon="sparkle"
        label={reason ? `${label} · ${reason}` : label}
        shortcut="mod+j"
        variant="ghost"
        onClick={run}
        className={className}
        data-testid="ai-ask"
      />
    );
  const button = (
    <Button
      icon="sparkle"
      variant={variant === 'rail' ? 'ghost' : 'secondary'}
      onClick={run}
      className={`${variant === 'rail' ? 'aic-ask-rail' : ''} ${className ?? ''}`}
      data-testid="ai-ask"
      data-limited={limited ? 'true' : undefined}
      {...(compact ? {} : { shortcut: formatShortcut('mod+j') })}
    >
      {label}
    </Button>
  );
  return reason ? <Tooltip content={reason}>{button}</Tooltip> : button;
}
